import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  Stack,
  Divider
} from '@mui/material';
import {
  Security as SecurityIcon,
  Warning as WarningIcon,
  Gavel as GavelIcon,
  Shield as ShieldIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import SEOErrorBoundary from '../components/SEOErrorBoundary';
import useStructuredData from '../hooks/useStructuredData';

const WhyMonitoringMatters: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { getEducationalPageData } = useStructuredData();

  const pageTitle = t('whyMonitoring.title');
  const pageDescription = i18n.language === 'en'
    ? 'Learn about identity theft risks and why monitoring legal publications is crucial for protecting your personal information in Costa Rica. Understand the real consequences of not monitoring public records.'
    : 'Conoce los riesgos del robo de identidad y por qué monitorear las publicaciones legales es crucial para proteger tu información personal en Costa Rica. Entiende las consecuencias reales de no monitorear los registros públicos.';

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://alertalegal.co';
  const canonicalUrl = `${siteUrl}${t('routes.whyMonitoringMatters')}`;
  const structuredData = getEducationalPageData(pageTitle, pageDescription, t('routes.whyMonitoringMatters'));

  const risks = [
    {
      icon: <GavelIcon sx={{ fontSize: 40, color: 'error.main' }} />,
      title: t('whyMonitoring.risks.legalDebt.title'),
      description: t('whyMonitoring.risks.legalDebt.description'),
    },
    {
      icon: <WarningIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      title: t('whyMonitoring.risks.impersonation.title'),
      description: t('whyMonitoring.risks.impersonation.description'),
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'error.main' }} />,
      title: t('whyMonitoring.risks.fraudulentActivity.title'),
      description: t('whyMonitoring.risks.fraudulentActivity.description'),
    },
  ];

  const benefits = [
    {
      icon: <ScheduleIcon sx={{ fontSize: 32, color: 'success.main' }} />,
      title: t('whyMonitoring.benefits.earlyDetection.title'),
      description: t('whyMonitoring.benefits.earlyDetection.description'),
    },
    {
      icon: <ShieldIcon sx={{ fontSize: 32, color: 'success.main' }} />,
      title: t('whyMonitoring.benefits.protection.title'),
      description: t('whyMonitoring.benefits.protection.description'),
    },
    {
      icon: <VisibilityIcon sx={{ fontSize: 32, color: 'success.main' }} />,
      title: t('whyMonitoring.benefits.transparency.title'),
      description: t('whyMonitoring.benefits.transparency.description'),
    },
  ];

  return (
    <>
      <SEOErrorBoundary fallbackTitle="Why Monitoring Matters - AlertaLegal" fallbackDescription="Learn about identity theft risks and why monitoring legal publications is crucial for protecting your personal information in Costa Rica.">
        <SEO 
          title={pageTitle}
          description={pageDescription}
          canonical={canonicalUrl}
          structuredData={structuredData}
          hreflangEnabled={true}
        />
      </SEOErrorBoundary>
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            align="center"
            tabIndex={-1}
            id="main-heading"
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 3,
              '&:focus': {
                outline: 'none'
              }
            }}
          >
            {t('whyMonitoring.title')}
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 6, 
              textAlign: 'center', 
              color: 'text.secondary',
              fontSize: '1.2rem',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            {t('whyMonitoring.subtitle')}
          </Typography>

          {/* Alert Section */}
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 6, 
              maxWidth: '700px', 
              mx: 'auto',
              fontSize: '1rem',
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
              {t('whyMonitoring.alertTitle')}
            </Typography>
            <Typography variant="body2">
              {t('whyMonitoring.alertMessage')}
            </Typography>
          </Alert>

          {/* Introduction */}
          <Card sx={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', mb: 6 }}>
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                {t('whyMonitoring.introduction.title')}
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                {t('whyMonitoring.introduction.content')}
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                {t('whyMonitoring.introduction.costaRicaContext')}
              </Typography>
            </CardContent>
          </Card>

          {/* Real Risks Section */}
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              align="center"
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                mb: 4 
              }}
            >
              {t('whyMonitoring.risksSection.title')}
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gap: 4, 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }
            }}>
              {risks.map((risk, index) => (
                <Box key={index}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      textAlign: 'center',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 15px -3px rgba(0, 0, 0, 0.15)',
                      }
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ mb: 3 }}>
                        {risk.icon}
                      </Box>
                      <Typography 
                        variant="h6" 
                        component="h3" 
                        gutterBottom 
                        sx={{ fontWeight: 600, color: 'primary.main' }}
                      >
                        {risk.title}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                        {risk.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* Benefits Section */}
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              align="center"
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                mb: 4 
              }}
            >
              {t('whyMonitoring.benefitsSection.title')}
            </Typography>
            
            <Stack spacing={4} sx={{ maxWidth: '800px', mx: 'auto' }}>
              {benefits.map((benefit, index) => (
                <Card 
                  key={index}
                  sx={{ 
                    boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                      boxShadow: '0 4px 8px -2px rgba(0, 0, 0, 0.15)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                      <Box sx={{ flexShrink: 0, mt: 0.5 }}>
                        {benefit.icon}
                      </Box>
                      <Box>
                        <Typography 
                          variant="h6" 
                          component="h3" 
                          gutterBottom 
                          sx={{ fontWeight: 600, color: 'primary.main' }}
                        >
                          {benefit.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                          {benefit.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>

          {/* Call to Action */}
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)',
              color: 'white',
              textAlign: 'center',
              boxShadow: '0 8px 16px -4px rgba(0, 0, 0, 0.2)'
            }}
          >
            <CardContent sx={{ p: { xs: 4, md: 6 } }}>
              <Typography 
                variant="h5" 
                component="h2" 
                gutterBottom 
                sx={{ fontWeight: 600, mb: 3 }}
              >
                {t('whyMonitoring.cta.title')}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: '1.1rem', 
                  lineHeight: 1.6,
                  maxWidth: '600px',
                  mx: 'auto'
                }}
              >
                {t('whyMonitoring.cta.description')}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </>
  );
};

export default WhyMonitoringMatters;